import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { 
  Menu, X, Home, Target, Network, DollarSign, Briefcase, Globe, Shield, 
  FileText, AlertCircle, Leaf, FlaskConical, Download, Settings, LayoutGrid, 
  Activity, Code, Building, Book, LogOut, ChevronLeft, ChevronRight, 
  ChevronDown, Bell, Search, Sun, Moon, Users, UserCheck, Landmark,
  Keyboard, Sparkles, Clock, Wrench, FolderKanban, Smartphone, Handshake, 
  Factory, Truck, Calculator, FileSearch, Key, CheckSquare, RefreshCw,
  Gift, Flame, Map, MapPin, BarChart3, AlertTriangle, Building2, Zap,
  CheckCircle, Database, Eye, TrendingUp, Award, Tag
} from 'lucide-react';
import NotificationCenter from './enhanced/NotificationCenter';
import ThemeToggle from './ThemeToggle';
import GlobalSearch from './advanced/GlobalSearch';
import Breadcrumbs from './navigation/Breadcrumbs';
import { SkipLinks } from './accessibility';
import { BadgeMadeInBharat, LogoLockup } from './shared';
import useAutoLogout from '../hooks/useAutoLogout';

/**
 * Enhanced Layout - Main application layout with accessibility
 * Features:
 * - WCAG 2.1 AA compliant
 * - Mobile-first responsive design
 * - Keyboard navigation
 * - Skip links for screen readers
 * - Collapsible sidebar
 * - Touch-optimized for mobile
 */

const Layout = ({ children }) => {
  // Responsive sidebar state - collapsed on mobile by default
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  
  // Enable auto-logout
  useAutoLogout();

  // Count NEW badges for "What's New" counter
  const countNewBadges = (items) => {
    let count = 0;
    items.forEach(item => {
      if (item.badge === 'NEW') count++;
      if (item.children) count += countNewBadges(item.children);
    });
    return count;
  };

  // Format last login time
  const formatLastLogin = () => {
    if (user?.lastLogin) {
      const lastLogin = new Date(user.lastLogin);
      // Check if date is valid
      if (isNaN(lastLogin.getTime())) {
        return 'Just now';
      }
      const now = new Date();
      const diffMs = now - lastLogin;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    }
    return 'Just now';
  };

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Keyboard shortcut listener for shortcuts modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '?' && e.shiftKey) {
        e.preventDefault();
        setShowShortcutsModal(true);
      }
      if (e.key === 'Escape') {
        setShowShortcutsModal(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Navigation structure - Optimized workflow-based grouping with max 2 levels
  const navigation = [
    // ============ CORE DASHBOARD ============
    { 
      name: 'Executive Dashboard', 
      path: '/', 
      icon: Home, 
      priority: 'p0', 
      isFeature: true 
    },

    // ============ AI FEATURES HUB ============
    { 
      name: 'AI Features', 
      path: '/ai', 
      icon: Sparkles, 
      priority: 'p0', 
      badge: 'NEW',
      isFeature: true,
      description: '10 AI-powered automation tools'
    },

    // ============ INSTALLATION OS ============
    {
      name: 'Installation OS',
      icon: Wrench,
      priority: 'p0',
      isCategory: true,
      children: [
        { 
          name: 'Pipeline Dashboard', 
          path: '/installation', 
          icon: LayoutGrid,
          isFeature: true,
          description: 'Site → Live Charger workflow'
        },
        { 
          name: 'All Projects', 
          path: '/installation/dashboard', 
          icon: FolderKanban,
          isFeature: true 
        },
        { 
          name: 'Sites Management', 
          path: '/installation/sites/new', 
          icon: MapPin,
          isFeature: true 
        },
        { 
          name: 'Analytics & Reports', 
          path: '/installation/analytics', 
          icon: BarChart3,
          isFeature: true 
        },
        { 
          name: 'Field Operations', 
          path: '/field', 
          icon: Smartphone,
          badge: 'MOBILE',
          isFeature: true 
        }
      ]
    },

    // ============ URGAA MODULE ============
    {
      name: 'URGAA - EV Ecosystem',
      icon: Zap,
      priority: 'p0',
      badge: 'NEW',
      isCategory: true,
      description: 'EV Platform & Services',
      children: [
        { 
          name: 'URGAA Dashboard', 
          path: '/urgaa', 
          icon: LayoutGrid,
          isFeature: true,
          description: 'Platform overview'
        },
        { 
          name: 'TCO Calculator', 
          path: '/urgaa/tco-calculator', 
          icon: Calculator,
          badge: 'NEW',
          isFeature: true 
        },
        { 
          name: 'Policy Alerts', 
          path: '/urgaa/policy-alerts', 
          icon: Bell,
          badge: 'NEW',
          isFeature: true 
        },
        { 
          name: 'Battery Health Monitor', 
          path: '/urgaa/battery-health', 
          icon: Activity,
          badge: 'NEW',
          isFeature: true 
        },
        { 
          name: 'EV Financing Marketplace', 
          path: '/urgaa/ev-financing', 
          icon: DollarSign,
          badge: 'NEW',
          isFeature: true 
        },
        { 
          name: 'EV Insurance Hub', 
          path: '/urgaa/ev-insurance', 
          icon: Shield,
          badge: 'NEW',
          isFeature: true 
        },
        { 
          name: 'Grid Monitoring', 
          path: '/urgaa/grid-monitoring', 
          icon: Network,
          badge: 'NEW',
          isFeature: true 
        },
        { 
          name: 'Roadside Assistance', 
          path: '/urgaa/roadside-assistance', 
          icon: Truck,
          badge: 'NEW',
          isFeature: true 
        }
      ]
    },

    // ============ REGULATORY & COMPLIANCE ============
    {
      name: 'Regulatory & Compliance',
      icon: FileText,
      priority: 'p0',
      isCategory: true,
      children: [
        { 
          name: 'Applications Manager', 
          path: '/urgaa/applications', 
          icon: FileText,
          isFeature: true 
        },
        { 
          name: 'Compliance Checker', 
          path: '/urgaa/compliance', 
          icon: CheckCircle,
          isFeature: true 
        },
        { 
          name: 'Site Intelligence', 
          path: '/urgaa/sites', 
          icon: MapPin,
          isFeature: true 
        },
        { 
          name: 'Workflows', 
          path: '/urgaa/workflows', 
          icon: FolderKanban,
          isFeature: true 
        },
        { 
          name: 'Asset Management', 
          path: '/urgaa/assets', 
          icon: Building2,
          isFeature: true 
        },
        { 
          name: 'Subsidies Manager', 
          path: '/urgaa/subsidies', 
          icon: DollarSign,
          isFeature: true 
        },
        { 
          name: 'Analytics Dashboard', 
          path: '/urgaa/analytics', 
          icon: BarChart3,
          isFeature: true 
        }
      ]
    },

    // ============ OPERATIONS & NETWORK ============
    {
      name: 'Operations & Network',
      icon: Activity,
      priority: 'p1',
      isCategory: true,
      children: [
        { name: 'Operations Dashboard', path: '/ops/dashboard', icon: LayoutGrid, isFeature: true },
        { name: 'Network Operations', path: '/network', icon: Network, isFeature: true },
        { name: 'Stations Management', path: '/network/stations-enhanced', icon: Building2, isFeature: true },
        { name: 'Health Map', path: '/network/health-map', icon: Map, isFeature: true },
        { name: 'Demand Heatmap', path: '/network/demand-heatmap', icon: Flame, badge: 'AI', isFeature: true },
        { name: 'Predictive Alerts', path: '/ops/predictive-alerts', icon: AlertTriangle, badge: 'AI', isFeature: true },
        { name: 'Incidents Manager', path: '/incidents', icon: AlertCircle, isFeature: true }
      ]
    },

    // ============ FINANCE & REVENUE ============
    {
      name: 'Finance & Revenue',
      icon: DollarSign,
      priority: 'p1',
      isCategory: true,
      children: [
        { name: 'Finance Dashboard', path: '/finance/dashboard', icon: LayoutGrid, isFeature: true },
        { name: 'Revenue Tracking', path: '/revenue', icon: TrendingUp, isFeature: true },
        { name: 'Credit Scorecard', path: '/finance/credit-scorecard', icon: Award, badge: 'AI', isFeature: true },
        { name: 'Pricing Matrix', path: '/pricing', icon: Tag, isFeature: true },
        { name: 'E-Invoicing', path: '/finance/einvoicing', icon: FileText, isFeature: true }
      ]
    },

    // ============ FLEET & CUSTOMERS ============
    {
      name: 'Fleet & Customers',
      icon: Truck,
      priority: 'p1',
      isCategory: true,
      children: [
        { name: 'Fleet Dashboard', path: '/fleet/dashboard', icon: LayoutGrid, isFeature: true },
        { name: 'Fleet Optimizer', path: '/urgaa/fleet', icon: Truck, isFeature: true },
        { name: 'Fleet APIs', path: '/fleet-apis', icon: Code, isFeature: true },
        { name: 'Host Portal', path: '/host', icon: Building, isFeature: true },
        { name: 'Roaming Network', path: '/roaming', icon: Globe, isFeature: true },
        { name: 'Programs & Benefits', path: '/programs', icon: Gift, isFeature: true },
        { name: 'Residential Services', path: '/urgaa/residential', icon: Building2, isFeature: true }
      ]
    },

    // ============ PARTNERSHIPS & OEM ============
    {
      name: 'Partnerships & OEM',
      icon: Handshake,
      priority: 'p1',
      badge: 'NEW',
      isCategory: true,
      children: [
        { name: 'Partner Dashboard', path: '/partnerships/dashboard', icon: LayoutGrid, badge: 'NEW', isFeature: true },
        { name: 'OEM Partners', path: '/partnerships/oem-partners', icon: Factory, badge: 'NEW', isFeature: true },
        { name: 'Proposals Manager', path: '/partnerships/proposals', icon: FileText, isFeature: true }
      ]
    },

    // ============ GOVERNMENT & POLICY ============
    {
      name: 'Government & Policy',
      icon: Landmark,
      priority: 'p2',
      isCategory: true,
      children: [
        { name: 'Government Planner', path: '/gov/planner', icon: MapPin, isFeature: true },
        { name: 'DISCOM Board', path: '/gov/discom', icon: Zap, isFeature: true },
        { name: 'Subsidy Calculator', path: '/govt/subsidy-calculator', icon: Calculator, badge: 'AI', isFeature: true },
        { name: 'Subsidy Verifier', path: '/gov/subsidy', icon: CheckCircle, isFeature: true },
        { name: 'Open Data Feeds', path: '/gov/opendata', icon: Database, isFeature: true },
        { name: 'Status Snapshot', path: '/gov/snapshot', icon: Eye, isFeature: true },
        { name: 'Policies & Documents', path: '/gov/policies', icon: FileText, isFeature: true }
      ]
    },

    // ============ PLANNING & STRATEGY ============
    {
      name: 'Planning & Strategy',
      icon: Target,
      priority: 'p2',
      isCategory: true,
      children: [
        { name: 'DSAT Targets', path: '/planning/dsat', icon: Target, isFeature: true },
        { name: 'Governance Dashboard', path: '/planning/governance', icon: Shield, isFeature: true },
        { name: 'SLA Credits', path: '/planning/sla-credits', icon: DollarSign, isFeature: true },
        { name: 'Pilot Analytics', path: '/pilot', icon: FlaskConical, isFeature: true }
      ]
    },

    // ============ ANALYTICS & REPORTING ============
    {
      name: 'Analytics & Reports',
      icon: BarChart3,
      priority: 'p2',
      isCategory: true,
      children: [
        { name: 'Advanced Analytics', path: '/analytics', icon: BarChart3, isFeature: true },
        { name: 'Data Exports', path: '/exports', icon: Download, isFeature: true },
        { name: 'ESG Tracking', path: '/esg', icon: Leaf, isFeature: true }
      ]
    },

    // ============ COMPLIANCE & AUDIT ============
    {
      name: 'Compliance & Audit',
      icon: Shield,
      priority: 'p2',
      isCategory: true,
      children: [
        { name: 'Compliance Dashboard', path: '/compliance', icon: Shield, isFeature: true },
        { name: 'Audit Trail', path: '/admin/audit', icon: FileSearch, isFeature: true }
      ]
    },

    // ============ SYSTEM & ADMIN ============
    {
      name: 'System & Admin',
      icon: Settings,
      priority: 'p3',
      isCategory: true,
      children: [
        { name: 'User Management', path: '/admin/users', icon: Users, isFeature: true },
        { name: 'Permissions Matrix', path: '/admin/permissions', icon: Key, isFeature: true },
        { name: 'Approval Workflows', path: '/admin/approvals', icon: CheckSquare, isFeature: true },
        { name: 'System Reliability', path: '/admin/reliability', icon: Activity, isFeature: true },
        { name: 'Settings', path: '/admin', icon: Settings, isFeature: true }
      ]
    },

    // ============ RESOURCES & TOOLS ============
    {
      name: 'Resources & Tools',
      icon: Book,
      priority: 'p3',
      isCategory: true,
      children: [
        { name: 'Master Workbook', path: '/workbook', icon: Book, isFeature: true },
        { name: 'Component Showcase', path: '/showcase', icon: LayoutGrid, isFeature: true },
        { name: 'Lifecycle Manager', path: '/lifecycle', icon: RefreshCw, isFeature: true }
      ]
    }
  ];

  const isActive = (path) => location.pathname === path;
  
  const isChildActive = (children) => {
    if (!children) return false;
    return children.some(child => {
      if (child.path) return location.pathname === child.path;
      if (child.children) return isChildActive(child.children);
      return false;
    });
  };

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  // Keyboard handler for menu items
  const handleKeyDown = useCallback((e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  }, []);

  // Calculate sidebar width
  const sidebarWidth = sidebarCollapsed ? 80 : 256;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Skip Links for Accessibility */}
      <SkipLinks />
      
      {/* Keyboard Shortcuts Modal */}
      {showShortcutsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowShortcutsModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Keyboard className="text-purple-600" size={24} />
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Keyboard Shortcuts</h2>
              </div>
              <button onClick={() => setShowShortcutsModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-300">Open Command Palette</span>
                <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono">Ctrl + K</kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-300">Global Search</span>
                <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono">/</kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-300">Toggle Dark Mode</span>
                <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono">Ctrl + D</kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-300">Go to Dashboard</span>
                <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono">G then D</kbd>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-300">Show Shortcuts</span>
                <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono">Shift + ?</kbd>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-600 dark:text-gray-300">Close Modal</span>
                <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm font-mono">Esc</kbd>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Press <kbd className="px-1 bg-gray-100 dark:bg-gray-700 rounded text-xs">Shift + ?</kbd> anytime to view shortcuts
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      
      {/* Sidebar */}
      <aside
        id="navigation"
        role="navigation"
        aria-label="Main navigation"
        className={cn(
          'fixed inset-y-0 left-0 z-50 border-r bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800',
          'transform transition-all duration-300 ease-in-out',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0'
        )}
        style={{ width: sidebarWidth }}
      >
        {/* Sidebar Header with Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-800">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3">
              <img 
                src="/assets/go4garage-logo-main.jpg" 
                alt="Go4Garage"
                className="h-10 w-auto object-contain"
              />
            </div>
          )}
          {sidebarCollapsed && (
            <img 
              src="/assets/go4garage-logo-main.jpg" 
              alt="Go4Garage"
              className="h-8 w-auto object-contain mx-auto"
            />
          )}
          
          {/* Mobile close button */}
          <button 
            onClick={() => setSidebarOpen(false)} 
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 touch-target"
            aria-label="Close navigation"
          >
            <X size={20} />
          </button>
          
          {/* Desktop collapse button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 touch-target"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>
        
        {/* Navigation Items */}
        <nav className="p-3 overflow-y-auto h-[calc(100vh-4rem)] scrollbar-thin">
          {/* Legend for navigation hierarchy */}
          {!sidebarCollapsed && (
            <div className="mb-4 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Navigation Legend</div>
              <div className="flex flex-wrap gap-2 text-[10px]">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-600"></span>
                  <span className="text-gray-600 dark:text-gray-400">Category</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#6B21A8' }}></span>
                  <span className="text-gray-600 dark:text-gray-400">Section</span>
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#991B1B' }}></span>
                  <span className="text-gray-600 dark:text-gray-400">Feature</span>
                </span>
              </div>
            </div>
          )}
          
          <ul role="menubar" className="space-y-2">
            {navigation.map((item) => (
              <li key={item.name} role="none">
                {/* Category Header - Main portal sections */}
                {item.isCategory && !sidebarCollapsed && (
                  <div className="px-3 py-3 mb-2 mt-4 bg-gradient-to-r from-green-50 to-transparent dark:from-green-900/20 rounded-lg border-l-4 border-green-600">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-600 animate-pulse"></span>
                      <item.icon size={16} className="text-green-600" />
                      <span className="text-xs font-extrabold uppercase tracking-wider text-green-700 dark:text-green-400">
                        {item.name}
                      </span>
                    </div>
                  </div>
                )}
                
                {item.children ? (
                  // Category with children - render all children
                  <div className={cn(item.isCategory && 'ml-1 space-y-1')}>
                    {item.children.map((child) => (
                      child.children ? (
                        // Subheader with nested features - Go4Garage Purple theme
                        <div key={child.name} className="mb-1">
                          <button
                            onClick={() => toggleMenu(child.name)}
                            onKeyDown={(e) => handleKeyDown(e, () => toggleMenu(child.name))}
                            className={cn(
                              'w-full flex items-center gap-3 px-3 py-2.5 text-sm font-semibold rounded-xl',
                              'transition-all duration-200 touch-target',
                              'border-l-2',
                              isChildActive(child.children) 
                                ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-600' 
                                : 'text-gray-700 dark:text-gray-300 border-transparent hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-purple-400',
                              sidebarCollapsed && 'justify-center'
                            )}
                            role="menuitem"
                            aria-expanded={expandedMenus[child.name]}
                            aria-haspopup="true"
                            title={sidebarCollapsed ? child.name : undefined}
                          >
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#6B21A8' }}></span>
                            {child.icon && <child.icon size={16} className="text-purple-600 dark:text-purple-400" aria-hidden="true" />}
                            {!sidebarCollapsed && (
                              <>
                                <span className="flex-1 text-left">{child.name}</span>
                                <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded font-medium">
                                  SECTION
                                </span>
                                <ChevronDown 
                                  size={16} 
                                  className={cn(
                                    'transition-transform duration-200 text-purple-600',
                                    expandedMenus[child.name] && 'rotate-180'
                                  )}
                                  aria-hidden="true"
                                />
                              </>
                            )}
                          </button>
                          
                          {/* Nested feature items */}
                          {!sidebarCollapsed && (
                            <ul
                              role="menu"
                              className={cn(
                                'ml-6 mt-1 space-y-0.5 overflow-hidden transition-all duration-200 border-l border-purple-200 dark:border-purple-800',
                                expandedMenus[child.name] ? 'max-h-[500px] opacity-100 py-1' : 'max-h-0 opacity-0'
                              )}
                            >
                              {child.children.map((nestedChild) => (
                                <li key={nestedChild.path} role="none">
                                  <Link
                                    to={nestedChild.path}
                                    role="menuitem"
                                    className={cn(
                                      'flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all duration-150 touch-target ml-2',
                                      isActive(nestedChild.path)
                                        ? 'bg-urga-electricBlue text-white shadow-md'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                                    )}
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#991B1B' }}></span>
                                    <span className="flex-1">{nestedChild.name}</span>
                                    {nestedChild.badge && (
                                      <span className="px-1.5 py-0.5 text-[10px] font-bold bg-semantic-success text-white rounded animate-badge-pop">
                                        {nestedChild.badge}
                                      </span>
                                    )}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                        // Direct feature link under category - Dark Red dot
                        <Link
                          key={child.path}
                          to={child.path}
                          role="menuitem"
                          className={cn(
                            'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-xl transition-all duration-150 touch-target',
                            'border-l-2',
                            isActive(child.path)
                              ? 'bg-urga-electricBlue text-white shadow-md border-urga-electricBlue'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border-transparent hover:border-red-300',
                            sidebarCollapsed && 'justify-center'
                          )}
                          title={sidebarCollapsed ? child.name : undefined}
                        >
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#991B1B' }}></span>
                          {child.icon && <child.icon size={16} aria-hidden="true" className="text-red-800 dark:text-red-400" />}
                          {!sidebarCollapsed && <span>{child.name}</span>}
                          {child.badge && !sidebarCollapsed && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-semantic-success text-white rounded animate-badge-pop">
                              {child.badge}
                            </span>
                          )}
                        </Link>
                      )
                    ))}
                  </div>
                ) : (
                  // Single menu item (Executive Dashboard - top level feature)
                  <Link
                    to={item.path}
                    role="menuitem"
                    className={cn(
                      'flex items-center gap-3 px-3 py-3 text-sm font-semibold rounded-xl',
                      'transition-all duration-150 touch-target',
                      'border-2',
                      isActive(item.path)
                        ? 'bg-urga-electricBlue text-white shadow-lg border-urga-electricBlue'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-urga-electricBlue',
                      sidebarCollapsed && 'justify-center'
                    )}
                    title={sidebarCollapsed ? item.name : undefined}
                  >
                    <item.icon size={18} aria-hidden="true" />
                    {!sidebarCollapsed && <span>{item.name}</span>}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>
        
        {/* Sidebar Footer */}
        {!sidebarCollapsed && (
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <BadgeMadeInBharat size="sm" />
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div 
        className={cn(
          'transition-all duration-300 min-h-screen',
          'lg:ml-64'
        )}
        style={{ 
          marginLeft: typeof window !== 'undefined' && window.innerWidth >= 1024 
            ? sidebarWidth 
            : 0 
        }}
      >
        {/* Header */}
        <header 
          className="sticky top-0 z-30 h-16 flex items-center justify-between px-4 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800"
          role="banner"
        >
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button 
              onClick={() => setSidebarOpen(true)} 
              className="p-2 lg:hidden rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 touch-target"
              aria-label="Open navigation menu"
              aria-expanded={sidebarOpen}
            >
              <Menu size={24} />
            </button>
            
            {/* Mobile logo - Go4Garage only */}
            <div className="flex items-center gap-1.5 lg:hidden">
              <img src="/assets/go4garage-logo-main.jpg" alt="Go4Garage" className="h-7" />
            </div>
          </div>
          
          <div className="flex items-center gap-2 md:gap-4">
            {/* Global Search */}
            <div id="search">
              <GlobalSearch />
            </div>
            
            {/* Keyboard Shortcuts Button */}
            <button
              onClick={() => setShowShortcutsModal(true)}
              className="hidden md:flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              title="Keyboard Shortcuts (Shift + ?)"
            >
              <Keyboard size={14} />
              <span className="hidden lg:inline">Shortcuts</span>
            </button>
            
            {/* Theme Toggle */}
            <ThemeToggle />
            
            {/* Notifications */}
            <NotificationCenter />
            
            {/* User Info with Last Login */}
            {user && (
              <div className="hidden md:flex items-center gap-3">
                <div className="flex flex-col items-end">
                  <span className="text-sm text-gray-600 dark:text-gray-400 truncate max-w-[150px]">
                    {user.email}
                  </span>
                  <div className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500">
                    <Clock size={10} />
                    <span>Last login: {formatLastLogin()}</span>
                  </div>
                </div>
                <span className="px-2 py-1 text-xs font-medium bg-urga-lightBlue text-urga-electricBlue rounded-lg">
                  {user.role}
                </span>
              </div>
            )}
            
            {/* Logout */}
            <button
              onClick={logout}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors touch-target"
              aria-label="Logout"
            >
              <LogOut size={16} aria-hidden="true" />
              <span className="hidden sm:inline">Logout</span>
            </button>
            
            {/* Version */}
            <span className="text-xs text-gray-400 hidden sm:inline">
              v2.0.0
            </span>
          </div>
        </header>

        {/* Page Content */}
        <main 
          id="main-content"
          role="main"
          className="p-4 md:p-6 overflow-x-hidden"
          tabIndex="-1"
        >
          <Breadcrumbs />
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;